/**
 * VideoContent - Vídeo com preview e player
 */
import { useState, useRef } from "react";
import { Play } from "lucide-react";
import { DialogTrigger } from "@/components/ui/dialog";
import { MediaLightbox } from "@/components/ui/media-lightbox";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";

export function VideoContent({ src, thumb, duration, caption, className }) {
  // Mesma regra da ImageContent: a legenda acompanha o tema resolvido em JS,
  // que é o mesmo que decide a cor de fundo da bolha.
  const { resolved: currentTheme } = useTheme();
  const isDark = currentTheme === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const previewRef = useRef(null);

  // Use thumb if available, otherwise we'll use a video element as preview
  const hasImageThumb =
    thumb &&
    !thumb.endsWith(".mp4") &&
    !thumb.endsWith(".3gp") &&
    !thumb.endsWith(".webm");

  return (
    <MediaLightbox
      open={isOpen}
      onOpenChange={setIsOpen}
      src={src}
      title={caption || "Vídeo"}
      type="video"
    >
      <DialogTrigger asChild>
        <div className={cn("cursor-pointer", className)}>
          <div className="rounded-lg overflow-hidden max-w-[280px] relative group/video">
            {/* Thumbnail com overlay de play */}
            <div className="relative">
              {hasImageThumb ? (
                <img
                  src={thumb}
                  alt={caption || "Vídeo"}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  style={{ minHeight: "120px" }}
                />
              ) : (
                <video
                  ref={previewRef}
                  src={src}
                  className="w-full h-auto object-cover"
                  style={{ minHeight: "120px" }}
                  muted
                  playsInline
                  preload="metadata"
                  onLoadedData={(e) => {
                    // Seek to first frame for thumbnail
                    e.target.currentTime = 0.1;
                  }}
                />
              )}
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover/video:bg-black/50 transition-colors">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play
                    className="h-6 w-6 text-neutral-900 ml-0.5"
                    fill="currentColor"
                  />
                </div>
              </div>
              {/* Duration badge */}
              {/* Duração: fica sobre um véu preto nos dois temas — branco fixo,
                  senão no tema claro o token vira quase preto sobre preto. */}
              {duration && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                  {duration}
                </div>
              )}
            </div>
          </div>
          {caption && (
            <p
              className={cn(
                "text-sm whitespace-pre-wrap break-words mt-1 px-1 max-w-[280px]",
                isDark ? "text-foreground-strong" : "text-gray-800",
              )}
            >
              {caption}
            </p>
          )}
        </div>
      </DialogTrigger>
    </MediaLightbox>
  );
}
